import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import FileIcon from "../../assets/image/file.svg";
import { mapViewToForm } from "./driverViewMap";
import PhoneNumberField from "../../Components/PhoneNumberField/PhoneNumberField";
import { FIXED_COUNTRY_CODE } from "../../utils/phone";
import SingleDatePicker from "../../Components/SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../assets/image/calendar-icon.svg";
import { useBackToList } from "../../hooks/useBackToList";

const toast = { error: notifyError, success: notifySuccess };

const MOBILE_IN_RE = /^[6-9]\d{9}$/;
const OTP_LEN = 6;
const OTP_TIMEOUT_SEC = 90;

const formatOtpCountdown = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const extractOtpPreviewFromSendResponse = (res) => {
  const raw = res?.data?.data;
  const cand =
    (typeof raw === "string" && raw) ||
    (raw && typeof raw === "object" ? raw.otp ?? raw.OTP ?? raw.temp_otp : null) ||
    res?.data?.otp;
  const digits = String(cand ?? "")
    .replace(/\D/g, "")
    .slice(0, OTP_LEN);
  return digits.length === OTP_LEN ? digits : "";
};

const documentTypeLabel = (documentType) => {
  const k = String(documentType ?? "").toLowerCase();
  if (k === "driver_license") return "Driver license (front)";
  if (k === "driver_license_back") return "Driver license (back)";
  if (k.includes("adhar_card_back") || k.includes("aadhar")) return "Aadhaar card back";
  if (k.includes("adhar_card_front") || k.includes("aadhar")) return "Aadhaar card front";
  if (k.includes("license") || k.includes("licence")) return "Driving license";
  if (!k) return "Document";
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const LicenseImageThumb = ({ previewUrl, imageError, onImageError, label = "Driving license" }) => (
  <div className="flex w-[30%] lg:w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="relative box-border flex h-20 lg:h-36 w-full min-h-0 shrink-0 items-center justify-center overflow-hidden bg-gray-100 p-2">
      {previewUrl && !imageError ? (
        <img
          src={previewUrl}
          alt="Driving license"
          className="box-border max-h-full max-w-full object-contain"
          loading="lazy"
          decoding="async"
          onError={onImageError}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
          <img src={FileIcon} className="w-8 opacity-60" alt="" />
          <span className="text-xs text-gray-500">No image</span>
        </div>
      )}
    </div>
    <span className="border-t border-gray-100 px-2 py-2 text-center text-xs md:text-sm font-UbuntuMedium text-gray-700">
      {label}
    </span>
  </div>
);

const LICENSE_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/jpg";
const LICENSE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

const validateLicenseImageFile = (file) => {
  if (!file.type.startsWith("image/")) {
    return "Please choose a JPEG, PNG, or WebP image";
  }
  if (file.size > LICENSE_IMAGE_MAX_BYTES) {
    return "Image must be 5 MB or smaller";
  }
  return "";
};

const LicenseUploadModal = ({
  open,
  frontPreviewUrl,
  backPreviewUrl,
  frontImageError,
  backImageError,
  onFrontImageError,
  onBackImageError,
  onPickFront,
  onPickBack,
  onContinue,
  onCancel,
  errorMessage,
  disabled,
  reasonLabel,
}) => {
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  if (!open) return null;

  const canContinue = Boolean(frontPreviewUrl && backPreviewUrl);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 font-UbuntuRegular"
      role="presentation"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white px-6 pb-8 pt-7 shadow-xl sm:px-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="license-upload-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h4
          id="license-upload-modal-title"
          className="mb-3 text-center text-xl font-UbuntuBold text-gray-900"
        >
          Upload new driving license
        </h4>
        <p className="mb-5 text-center text-sm leading-relaxed text-gray-600">
          {reasonLabel ||
            "You changed the TR license expiry date. Please upload updated driver license front and back images to continue."}
        </p>

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-center text-sm font-UbuntuMedium text-gray-800">
              Driver license (front)
            </p>
            <div className="mx-auto flex h-40 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 p-2">
              {frontPreviewUrl && !frontImageError ? (
                <img
                  src={frontPreviewUrl}
                  alt="License front preview"
                  className="max-h-full max-w-full object-contain"
                  onError={onFrontImageError}
                />
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <img src={FileIcon} className="w-10 opacity-60" alt="" />
                  <span className="text-xs text-gray-500">No preview</span>
                </div>
              )}
            </div>
            <input
              ref={frontInputRef}
              type="file"
              accept={LICENSE_IMAGE_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPickFront(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-red-600 py-2.5 text-center text-sm font-UbuntuBold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              disabled={disabled}
              onClick={() => frontInputRef.current?.click()}
            >
              Choose front image
            </button>
          </div>

          <div>
            <p className="mb-2 text-center text-sm font-UbuntuMedium text-gray-800">
              Driver license (back)
            </p>
            <div className="mx-auto flex h-40 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 p-2">
              {backPreviewUrl && !backImageError ? (
                <img
                  src={backPreviewUrl}
                  alt="License back preview"
                  className="max-h-full max-w-full object-contain"
                  onError={onBackImageError}
                />
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <img src={FileIcon} className="w-10 opacity-60" alt="" />
                  <span className="text-xs text-gray-500">No preview</span>
                </div>
              )}
            </div>
            <input
              ref={backInputRef}
              type="file"
              accept={LICENSE_IMAGE_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPickBack(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-red-600 py-2.5 text-center text-sm font-UbuntuBold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              disabled={disabled}
              onClick={() => backInputRef.current?.click()}
            >
              Choose back image
            </button>
          </div>
        </div>

        {errorMessage ? (
          <p className="mb-4 text-center text-sm text-red-500">{errorMessage}</p>
        ) : null}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            className="w-full rounded-xl bg-red-600 py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled || !canContinue}
            onClick={onContinue}
          >
            Continue
          </button>
          <button
            type="button"
            className="w-full rounded-xl py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-50"
            style={{ backgroundColor: "#030253" }}
            disabled={disabled}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        <p className="mb-0 mt-4 text-center text-xs text-gray-500">
          JPEG, PNG, or WebP · Max 5 MB · Both front and back images are required
        </p>
      </div>
    </div>
  );
};

const EditDocumentThumb = ({ doc }) => {
  const [imgError, setImgError] = useState(false);
  const url =
    typeof doc?.document_image === "string" ? doc.document_image.trim() : "";
  const label = documentTypeLabel(doc?.document_type);

  return (
    <div className="flex w-[30%] lg:w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="relative box-border flex h-20 lg:h-36 w-full min-h-0 shrink-0 items-center justify-center overflow-hidden bg-gray-100 p-2">
        {url && !imgError ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-full max-h-full w-full max-w-full min-h-0 min-w-0 items-center justify-center rounded-md"
            aria-label={`Open ${label} in new tab`}
          >
            <img
              src={url}
              alt={label}
              className="box-border max-h-full max-w-full object-contain"
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
            />
          </a>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
            <img src={FileIcon} className="w-8 opacity-60" alt="" />
            <span className="text-xs text-gray-500">Preview unavailable</span>
          </div>
        )}
      </div>
      <span className="border-t border-gray-100 px-2 py-2 text-center text-xs md:text-sm font-UbuntuMedium text-gray-700">
        {label}
      </span>
    </div>
  );
};

const LICENSE_NUMBER_MAX_LENGTH = 15;
const LICENSE_NUMBER_PATTERN = /^[A-Z]{2}\d{13}$/;

const normalizeLicenseNumberInput = (value) =>
  String(value ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, LICENSE_NUMBER_MAX_LENGTH);

const getDateYearsAgoString = (years) => {
  const now = new Date();
  now.setFullYear(now.getFullYear() - years);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const maxLicenseCreationDate = getDateYearsAgoString(1);

const initialErrors = {
  mobile_number: "",
  license_number: "",
  license_creation: "",
  license_expiry: "",
  nt_license_expiry: "",
  license_image: "",
  age: "",
};

const isLicenseDocument = (doc) => {
  const k = String(doc?.document_type ?? "").toLowerCase();
  return k.includes("license") || k.includes("licence");
};

const findDocumentByType = (docs, type) =>
  (Array.isArray(docs) ? docs : []).find(
    (doc) => String(doc?.document_type ?? "").toLowerCase() === type
  );

const findLicenseFrontDocument = (docs) => {
  const front = findDocumentByType(docs, "driver_license");
  if (front) return front;
  return (Array.isArray(docs) ? docs : []).find((doc) => {
    const k = String(doc?.document_type ?? "").toLowerCase();
    return (k.includes("license") || k.includes("licence")) && !k.includes("back");
  });
};

const findLicenseBackDocument = (docs) => findDocumentByType(docs, "driver_license_back");

const EditDriverDetails = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const driverId = (searchParams.get("driver_id") || "").trim();
  const backToList = useBackToList("/driver-management");

  const [loadingDriver, setLoadingDriver] = useState(() => Boolean(driverId));
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(initialErrors);

  const [fullName, setFullName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseCreation, setLicenseCreation] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [ntLicenseExpiry, setNtLicenseExpiry] = useState("");
  const [adharNumber, setAdharNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [age, setAge] = useState("");
  const [accountantName, setAccountantName] = useState("");
  const [miplLocation, setMiplLocation] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [documentData, setDocumentData] = useState([]);
  const [licenseImageUrl, setLicenseImageUrl] = useState("");
  const [licenseImagePreview, setLicenseImagePreview] = useState("");
  const [licenseImageFile, setLicenseImageFile] = useState(null);
  const [licenseImageBroken, setLicenseImageBroken] = useState(false);
  const [licenseBackImageUrl, setLicenseBackImageUrl] = useState("");
  const [licenseBackImagePreview, setLicenseBackImagePreview] = useState("");
  const [licenseBackImageFile, setLicenseBackImageFile] = useState(null);
  const [licenseBackImageBroken, setLicenseBackImageBroken] = useState(false);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const licensePreviewObjectUrlRef = useRef(null);
  const licenseBackPreviewObjectUrlRef = useRef(null);

  const [baselineLicenseExpiry, setBaselineLicenseExpiry] = useState("");
  const [baselineNtLicenseExpiry, setBaselineNtLicenseExpiry] = useState("");
  const [baselineMobileDigits, setBaselineMobileDigits] = useState(null);
  const [showLicenseUploadModal, setShowLicenseUploadModal] = useState(false);
  const [showMobileOtpModal, setShowMobileOtpModal] = useState(false);
  const [otpCells, setOtpCells] = useState(() => Array(OTP_LEN).fill(""));
  const [otpFromResponse, setOtpFromResponse] = useState("");
  const [otpModalSubmitting, setOtpModalSubmitting] = useState(false);
  const [otpTimeLeft, setOtpTimeLeft] = useState(0);
  const [otpResending, setOtpResending] = useState(false);
  const otpInputRefs = useRef([]);

  useEffect(() => {
    setSubheading("Edit Driver Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(
    () => () => {
      if (licensePreviewObjectUrlRef.current) {
        URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
        licensePreviewObjectUrlRef.current = null;
      }
      if (licenseBackPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
        licenseBackPreviewObjectUrlRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const load = async () => {
      if (!driverId) {
        setLoadingDriver(false);
        return;
      }
      setLoadingDriver(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewDriver}?driver_id=${encodeURIComponent(driverId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const form = mapViewToForm(d);
          if (form) {
            setFullName(form.fullName);
            setLicenseNumber(form.licenseNumber);
            setLicenseCreation(form.licenseCreation);
            setLicenseExpiry(form.licenseExpiry);
            setBaselineLicenseExpiry(form.licenseExpiry || "");
            setNtLicenseExpiry(form.ntLicenseExpiry);
            setBaselineNtLicenseExpiry(form.ntLicenseExpiry || "");
            setAdharNumber(form.adharNumber);
            setMobileNumber(form.mobile);
            setAge(form.age);
            setAccountantName(form.accountantName);
            setMiplLocation(form.miplLocation);
            setProfileImage(form.profileImage || "");
            const docs = Array.isArray(form.documentData) ? form.documentData : [];
            setDocumentData(docs);
            const licenseDoc = findLicenseFrontDocument(docs);
            const licenseBackDoc = findLicenseBackDocument(docs);
            const licenseUrl =
              typeof licenseDoc?.document_image === "string"
                ? licenseDoc.document_image.trim()
                : "";
            const licenseBackUrl =
              typeof licenseBackDoc?.document_image === "string"
                ? licenseBackDoc.document_image.trim()
                : "";
            setLicenseImageUrl(licenseUrl);
            setLicenseImagePreview(licenseUrl);
            setLicenseBackImageUrl(licenseBackUrl);
            setLicenseBackImagePreview(licenseBackUrl);
            setLicenseBackImageBroken(false);
            setLicenseImageFile(null);
            setLicenseBackImageFile(null);
            setLicenseImageBroken(false);
            if (licensePreviewObjectUrlRef.current) {
              URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
              licensePreviewObjectUrlRef.current = null;
            }
            if (licenseBackPreviewObjectUrlRef.current) {
              URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
              licenseBackPreviewObjectUrlRef.current = null;
            }
            setProfileImageBroken(false);
            setBaselineMobileDigits((form.mobile || "").replace(/\D/g, "").slice(0, 10));
            setShowMobileOtpModal(false);
            setShowLicenseUploadModal(false);
            setOtpCells(Array(OTP_LEN).fill(""));
            setOtpFromResponse("");
          }
        } else {
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load driver";
          toast.error(errMsg);
        }
      } catch {
        toast.error("Failed to load driver");
      } finally {
        setLoadingDriver(false);
      }
    };
    load();
  }, [driverId]);

  const mobileDigits = (mobileNumber || "").replace(/\D/g, "").slice(0, 10);
  const mobileChanged =
    baselineMobileDigits != null &&
    baselineMobileDigits !== "" &&
    mobileDigits !== baselineMobileDigits;

  useEffect(() => {
    if (!mobileChanged) {
      setShowMobileOtpModal(false);
      setOtpCells(Array(OTP_LEN).fill(""));
      setOtpFromResponse("");
    }
  }, [mobileChanged]);

  const requestSendDriverMobileOtp = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await handleApiCall("post", endpoint.sendDriverMobileOtp, {
        // driver_id: driverId,
        country_code: FIXED_COUNTRY_CODE,
        mobile_number: mobileDigits,
        type: "Driver Verification",
      });
      if (r?.data?.code === 200) {
        const preview = extractOtpPreviewFromSendResponse(r);
        if (preview) setOtpFromResponse(preview);
      }
    } catch {
      /* optional route */
    }
  }, [driverId, mobileDigits]);

  useEffect(() => {
    if (!showMobileOtpModal || !driverId) return undefined;
    setOtpCells(Array(OTP_LEN).fill(""));
    setOtpFromResponse("");
    setOtpTimeLeft(OTP_TIMEOUT_SEC);
    requestSendDriverMobileOtp();
    const focusT = window.setTimeout(() => otpInputRefs.current[0]?.focus(), 80);
    const intervalId = window.setInterval(() => {
      setOtpTimeLeft((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => {
      window.clearTimeout(focusT);
      window.clearInterval(intervalId);
    };
  }, [showMobileOtpModal, driverId, requestSendDriverMobileOtp]);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const clearUploadedLicenseFiles = useCallback(() => {
    if (licensePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
      licensePreviewObjectUrlRef.current = null;
    }
    if (licenseBackPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
      licenseBackPreviewObjectUrlRef.current = null;
    }
    setLicenseImageFile(null);
    setLicenseBackImageFile(null);
    setLicenseImagePreview(licenseImageUrl);
    setLicenseBackImagePreview(licenseBackImageUrl);
    setLicenseImageBroken(false);
    setLicenseBackImageBroken(false);
  }, [licenseImageUrl, licenseBackImageUrl]);

  const handleLicenseImagePick = (file) => {
    const validationError = validateLicenseImageFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, license_image: validationError }));
      return;
    }
    if (licensePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    licensePreviewObjectUrlRef.current = objectUrl;
    setLicenseImageFile(file);
    setLicenseImagePreview(objectUrl);
    setLicenseImageBroken(false);
    clearFieldError("license_image");
  };

  const handleLicenseBackImagePick = (file) => {
    const validationError = validateLicenseImageFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, license_image: validationError }));
      return;
    }
    if (licenseBackPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    licenseBackPreviewObjectUrlRef.current = objectUrl;
    setLicenseBackImageFile(file);
    setLicenseBackImagePreview(objectUrl);
    setLicenseBackImageBroken(false);
    clearFieldError("license_image");
  };

  const getLicenseUploadReason = useCallback(
    (trValue, ntValue) => {
      const trChanged = (trValue || "").trim() !== (baselineLicenseExpiry || "").trim();
      const ntChanged = (ntValue || "").trim() !== (baselineNtLicenseExpiry || "").trim();
      if (trChanged && ntChanged) {
        return "You changed the TR and NT license expiry dates. Please upload updated driver license front and back images to continue.";
      }
      if (trChanged) {
        return "You changed the TR license expiry date. Please upload updated driver license front and back images to continue.";
      }
      if (ntChanged) {
        return "You changed the NT license expiry date. Please upload updated driver license front and back images to continue.";
      }
      return "";
    },
    [baselineLicenseExpiry, baselineNtLicenseExpiry]
  );

  const openLicenseUploadModal = useCallback(() => {
    setShowLicenseUploadModal(true);
  }, []);

  const closeLicenseUploadModal = useCallback(() => {
    setShowLicenseUploadModal(false);
    setLicenseExpiry(baselineLicenseExpiry);
    setNtLicenseExpiry(baselineNtLicenseExpiry);
    clearUploadedLicenseFiles();
    clearFieldError("license_image");
  }, [baselineLicenseExpiry, baselineNtLicenseExpiry, clearUploadedLicenseFiles]);

  const continueLicenseUploadModal = useCallback(() => {
    if (!licenseImageFile || !licenseBackImageFile) {
      setErrors((prev) => ({
        ...prev,
        license_image: "Upload both driver license front and back images to continue",
      }));
      return;
    }
    setShowLicenseUploadModal(false);
    clearFieldError("license_image");
  }, [licenseImageFile, licenseBackImageFile]);

  const handleLicenseCreationChange = (nextDate) => {
    clearFieldError("license_creation");
    setLicenseCreation(nextDate);
  };

  const handleLicenseExpiryChange = (nextExpiry) => {
    const next = (nextExpiry || "").trim();
    const baseline = (baselineLicenseExpiry || "").trim();

    clearFieldError("license_expiry");
    setLicenseExpiry(nextExpiry);

    if (baseline && next && next !== baseline) {
      openLicenseUploadModal();
      return;
    }

    if (
      (nextExpiry || "").trim() === baseline &&
      (ntLicenseExpiry || "").trim() === (baselineNtLicenseExpiry || "").trim()
    ) {
      setShowLicenseUploadModal(false);
      clearUploadedLicenseFiles();
      clearFieldError("license_image");
    }
  };

  const handleNtLicenseExpiryChange = (nextExpiry) => {
    const next = (nextExpiry || "").trim();
    const baseline = (baselineNtLicenseExpiry || "").trim();

    clearFieldError("nt_license_expiry");
    setNtLicenseExpiry(nextExpiry);

    if (baseline && next && next !== baseline) {
      openLicenseUploadModal();
      return;
    }

    if (
      (nextExpiry || "").trim() === baseline &&
      (licenseExpiry || "").trim() === (baselineLicenseExpiry || "").trim()
    ) {
      setShowLicenseUploadModal(false);
      clearUploadedLicenseFiles();
      clearFieldError("license_image");
    }
  };

  const validate = () => {
    const next = { ...initialErrors };
    const mobile = (mobileNumber || "").replace(/\D/g, "");
    const license = (licenseNumber || "").trim();
    const expiry = (licenseExpiry || "").trim();
    const ntExpiry = (ntLicenseExpiry || "").trim();
    const ageValue = (age || "").trim();
    const ageNum = Number(ageValue);
    const trChanged = expiry !== (baselineLicenseExpiry || "").trim();
    const ntChanged = ntExpiry !== (baselineNtLicenseExpiry || "").trim();
    const licenseDocsRequired = trChanged || ntChanged;

    if (!license) next.license_number = "Driver license number is required";
    else if (!LICENSE_NUMBER_PATTERN.test(license)) {
      next.license_number = "Enter a valid driver license number.";
    }

    const creation = (licenseCreation || "").trim();
    if (!creation) {
      next.license_creation = "License creation date is required.";
    } else if (creation > maxLicenseCreationDate) {
      next.license_creation = "License creation date must be at least 1 year before today.";
    }

    if (!expiry) next.license_expiry = "TR license expiry date is required";
    else if (licenseCreation && expiry < licenseCreation) {
      next.license_expiry = "TR expiry date must be on or after creation date";
    }

    if (!ntExpiry) next.nt_license_expiry = "NT license expiry date is required";
    else if (licenseCreation && ntExpiry < licenseCreation) {
      next.nt_license_expiry = "NT expiry date must be on or after creation date";
    }

    if (licenseDocsRequired && (!licenseImageFile || !licenseBackImageFile)) {
      next.license_image =
        "Upload both driver license front and back images to change the expiry date";
      openLicenseUploadModal();
    }

    if (!mobile) next.mobile_number = "Mobile number is required";
    else if (!MOBILE_IN_RE.test(mobile)) {
      next.mobile_number = "Enter a valid 10-digit Indian mobile number";
    }

    if (ageValue && ageNum < 18) {
      next.age = "Age cannot be less than 18";
    }

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const buildEditPayload = () => ({
    driver_id: driverId,
    full_name: (fullName || "").trim(),
    license_number: (licenseNumber || "").trim(),
    license_creation_date: (licenseCreation || "").trim(),
    license_expiry_date: (licenseExpiry || "").trim(),
    nt_license_expiry_date: (ntLicenseExpiry || "").trim(),
    adhar_card_number: (adharNumber || "").trim(),
    mobile_number: (mobileNumber || "").replace(/\D/g, ""),
    country_code: FIXED_COUNTRY_CODE,
    age: Number((age || "").trim()),
  });

  const buildEditFormData = () => {
    const fields = buildEditPayload();
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value != null && value !== "") {
        formData.append(key, String(value));
      }
    });
    if (licenseImageFile) {
      formData.append("license_doc", licenseImageFile, licenseImageFile.name);
    }
    if (licenseBackImageFile) {
      formData.append("license_doc_back", licenseBackImageFile, licenseBackImageFile.name);
    }
    return formData;
  };

  const performSave = async () => {
    setSubmitting(true);
    try {
      const body =
        licenseImageFile || licenseBackImageFile ? buildEditFormData() : buildEditPayload();
      const response = await handleApiCall("put", endpoint.editDriver, body);
      if (response?.data?.code === 200) {
        toast.success(response?.data?.message || "Driver updated successfully");
        setTimeout(() => navigate(backToList), 1500);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to update driver";
      toast.error(errMsg);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const raw = e.clipboardData.getData("text") || "";
    const digits = raw.replace(/\D/g, "").slice(0, OTP_LEN);
    const next = Array(OTP_LEN).fill("");
    for (let i = 0; i < digits.length; i += 1) next[i] = digits[i];
    setOtpCells(next);
    otpInputRefs.current[Math.min(digits.length, OTP_LEN - 1)]?.focus();
  };

  const handleResendDriverOtp = async () => {
    if (otpTimeLeft > 0 || otpResending || otpModalSubmitting) return;
    setOtpResending(true);
    try {
      await requestSendDriverMobileOtp();
      setOtpTimeLeft(OTP_TIMEOUT_SEC);
      setOtpCells(Array(OTP_LEN).fill(""));
      toast.success("OTP sent again");
    } catch {
      toast.error("Could not resend OTP. Try again.");
    } finally {
      setOtpResending(false);
    }
  };

  const handleModalVerifyAndSave = async () => {
    if (!driverId) {
      toast.error("Driver ID is missing");
      return;
    }
    if (otpTimeLeft === 0) {
      toast.error("OTP expired. Please resend OTP and try again.");
      return;
    }
    const otp = otpCells.join("");
    if (otp.length !== OTP_LEN) {
      toast.error(`Enter the ${OTP_LEN}-digit OTP`);
      return;
    }
    setOtpModalSubmitting(true);
    try {
      const verifyRes = await handleApiCall("post", endpoint.verifyOtp, {
        // driver_id: driverId,
        country_code: FIXED_COUNTRY_CODE,
        mobile_number: mobileDigits,
        otp,
        type: "Driver Verification",
      });
      if (verifyRes?.data?.code !== 200) {
        const errMsg =
          verifyRes?.response?.data?.errorMessage ||
          verifyRes?.response?.data?.message ||
          verifyRes?.data?.message ||
          "OTP verification failed";
        toast.error(errMsg);
        return;
      }
      setShowMobileOtpModal(false);
      setOtpCells(Array(OTP_LEN).fill(""));
      setOtpFromResponse("");
      await performSave();
    } catch {
      toast.error("OTP verification failed. Please try again.");
    } finally {
      setOtpModalSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!driverId) {
      toast.error("Driver ID is missing");
      return;
    }
    if (!validate()) return;

    if (mobileChanged) {
      setShowMobileOtpModal(true);
      return;
    }

    await performSave();
  };

  const closeOtpModal = useCallback(() => {
    if (otpModalSubmitting) return;
    setShowMobileOtpModal(false);
    setOtpCells(Array(OTP_LEN).fill(""));
    setOtpFromResponse("");
    setOtpTimeLeft(0);
  }, [otpModalSubmitting]);

  useEffect(() => {
    if (!showMobileOtpModal) return undefined;
    const onKey = (ev) => {
      if (ev.key === "Escape") closeOtpModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showMobileOtpModal, otpModalSubmitting, closeOtpModal]);

  useEffect(() => {
    if (!showLicenseUploadModal) return undefined;
    const onKey = (ev) => {
      if (ev.key === "Escape") closeLicenseUploadModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showLicenseUploadModal, closeLicenseUploadModal]);

  const disabledForm =
    submitting || loadingDriver || !driverId || otpModalSubmitting || showLicenseUploadModal;

  const otherDocuments = useMemo(
    () => documentData.filter((doc) => !isLicenseDocument(doc)),
    [documentData]
  );

  const licensePreviewSrc = licenseImagePreview || licenseImageUrl;
  const licenseBackPreviewSrc = licenseBackImagePreview || licenseBackImageUrl;
  const licenseUploadReason = getLicenseUploadReason(licenseExpiry, ntLicenseExpiry);

  if (!driverId && !loadingDriver) {
    return (
      <>
        <Toastify />
        <div className="mx-auto mt-7 max-w-5xl px-4 font-UbuntuRegular">
          <p className="text-gray-600">
            Missing driver id. Open edit from the driver list.
          </p>
          <Link to={backToList} className="btn btn-primary mt-3">
            Back to list
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
     <div className='col-md-4'>
      <Link to={backToList}  className="btn btn-primary shrink-0 mt-4">Back</Link>
    </div>
      <Toastify />
      {showMobileOtpModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 font-UbuntuRegular"
          role="presentation"
          onClick={closeOtpModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white px-6 pb-8 pt-7 shadow-xl sm:px-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-driver-otp-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="edit-driver-otp-title"
              className="mb-3 text-center text-xl font-UbuntuBold text-gray-900"
            >
              Confirm with OTP
            </h4>
            <p className="mb-5 text-center text-sm leading-relaxed text-gray-600">
              Enter the {OTP_LEN}-digit OTP sent to{" "}
              <span className="font-UbuntuMedium text-gray-900">
                {FIXED_COUNTRY_CODE} {mobileDigits}
              </span>
              . After verification, your driver details will be saved.
            </p>

            <div className="mb-5 rounded-xl bg-gray-100 px-4 py-4 text-center">
              <p className="mb-2 text-[11px] font-UbuntuMedium uppercase tracking-wider text-gray-500">
                OTP from response
              </p>
              <p className="mb-0 text-2xl font-UbuntuBold tracking-[0.4em] text-blue-600">
                {otpFromResponse
                  ? otpFromResponse.split("").join(" ")
                  : "— — — — — —"}
              </p>
            </div>

            <div className="mb-4 flex justify-center gap-2 sm:gap-2.5">
              {Array.from({ length: OTP_LEN }, (_, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpInputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  aria-label={`Digit ${index + 1}`}
                  disabled={otpModalSubmitting}
                  value={otpCells[index]}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(-1);
                    setOtpCells((prev) => {
                      const next = [...prev];
                      next[index] = v;
                      return next;
                    });
                    if (v && index < OTP_LEN - 1) {
                      otpInputRefs.current[index + 1]?.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Backspace" || index === 0) return;
                    if (!otpCells[index]) {
                      e.preventDefault();
                      setOtpCells((prev) => {
                        const next = [...prev];
                        next[index - 1] = "";
                        return next;
                      });
                      otpInputRefs.current[index - 1]?.focus();
                    }
                  }}
                  className="h-12 w-10 rounded-lg border-2 border-gray-200 bg-white text-center text-lg font-UbuntuBold text-gray-900 outline-none transition-colors focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:opacity-50 sm:h-14 sm:w-11 sm:text-xl"
                />
              ))}
            </div>

            <p className="mb-5 text-center text-sm text-gray-600">
              Remaining time:{" "}
              <span className="font-UbuntuMedium text-gray-900">
                {formatOtpCountdown(otpTimeLeft)}s
              </span>
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                className="w-full rounded-xl bg-red-600 py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-50"
                disabled={otpModalSubmitting || otpResending || otpTimeLeft === 0}
                onClick={handleModalVerifyAndSave}
              >
                {otpModalSubmitting ? "Saving…" : "Verify"}
              </button>
              <button
                type="button"
                className="w-full rounded-xl py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-50"
                style={{ backgroundColor: "#030253" }}
                disabled={otpModalSubmitting}
                onClick={closeOtpModal}
              >
                Cancel
              </button>
            </div>

            <p className="mb-0 mt-5 text-center text-sm text-gray-600">
              Didn&apos;t get code?{" "}
              <button
                type="button"
                className={`inline border-0 bg-transparent p-0 text-sm font-UbuntuMedium underline-offset-2 ${
                  otpTimeLeft > 0 || otpResending || otpModalSubmitting
                    ? "cursor-not-allowed text-gray-400 no-underline"
                    : "cursor-pointer text-blue-600 hover:underline"
                }`}
                disabled={otpTimeLeft > 0 || otpResending || otpModalSubmitting}
                onClick={handleResendDriverOtp}
              >
                {otpResending ? "Sending…" : "Resend"}
              </button>
            </p>
          </div>
        </div>
      ) : null}
      <LicenseUploadModal
        open={showLicenseUploadModal}
        frontPreviewUrl={licenseImageFile ? licensePreviewSrc : ""}
        backPreviewUrl={licenseBackImageFile ? licenseBackPreviewSrc : ""}
        frontImageError={licenseImageBroken}
        backImageError={licenseBackImageBroken}
        onFrontImageError={() => setLicenseImageBroken(true)}
        onBackImageError={() => setLicenseBackImageBroken(true)}
        onPickFront={handleLicenseImagePick}
        onPickBack={handleLicenseBackImagePick}
        onContinue={continueLicenseUploadModal}
        onCancel={closeLicenseUploadModal}
        errorMessage={errors.license_image}
        disabled={submitting || loadingDriver || otpModalSubmitting}
        reasonLabel={licenseUploadReason}
      />
      <form onSubmit={onSubmit} noValidate>
        <div className="md:mt-7 text-center">
          <div className="mx-auto inline-flex h-28 w-28 md:h-40 md:w-40 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100">
            {!loadingDriver && profileImage && !profileImageBroken ? (
              <img
                src={profileImage}
                alt={fullName ? `${fullName} profile` : "Driver profile"}
                className="h-full w-full object-cover"
                onError={() => setProfileImageBroken(true)}
              />
            ) : !loadingDriver ? (
              <span className="text-4xl font-UbuntuMedium text-gray-400">
                {(fullName || "?").trim().slice(0, 1).toUpperCase() || "?"}
              </span>
            ) : null}
          </div>
        </div>
        <div className="mx-auto max-w-5xl md:px-4">
          {loadingDriver ? (
            <p className="my-4 text-gray-500">Loading driver…</p>
          ) : null}
          <div className="my-4 md:my-4 grid grid-cols-1 gap-3 font-UbuntuRegular sm:grid-cols-2 lg:grid-cols-3 [&_.form-control]:min-h-11 [&_.form-control:disabled]:bg-slate-200">
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={disabledForm}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">
                Driver license number
              </label>
              <input
                type="text"
                className={`form-control ${errors.license_number ? "border border-red-500" : ""}`}
                placeholder="Driver license number"
                value={licenseNumber}
                maxLength={LICENSE_NUMBER_MAX_LENGTH}
                onChange={(e) => {
                  setLicenseNumber(normalizeLicenseNumberInput(e.target.value));
                  clearFieldError("license_number");
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  setLicenseNumber(normalizeLicenseNumberInput(e.clipboardData.getData("text")));
                  clearFieldError("license_number");
                }}
                disabled={disabledForm}
                autoComplete="off"
              />
              {errors.license_number ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.license_number}</p>
              ) : null}
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="License creation date"
                value={licenseCreation}
                maxDate={maxLicenseCreationDate}
                onChange={handleLicenseCreationChange}
                disabled={disabledForm}
                filterIcon={CalendarIcon}
                className={errors.license_creation ? "[&_button]:border-red-500" : ""}
              />
              {errors.license_creation ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.license_creation}</p>
              ) : null}
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="TR License Expiry Date"
                value={licenseExpiry}
                minDate={licenseCreation || undefined}
                onChange={handleLicenseExpiryChange}
                disabled={disabledForm}
                filterIcon={CalendarIcon}
                className={errors.license_expiry ? "[&_button]:border-red-500" : ""}
              />
              {errors.license_expiry ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.license_expiry}</p>
              ) : null}
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="NT License Expiry Date"
                value={ntLicenseExpiry}
                minDate={licenseCreation || undefined}
                onChange={handleNtLicenseExpiryChange}
                disabled={disabledForm}
                filterIcon={CalendarIcon}
                className={errors.nt_license_expiry ? "[&_button]:border-red-500" : ""}
              />
              {errors.nt_license_expiry ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.nt_license_expiry}</p>
              ) : null}
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">
                Aadhaar card number
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Aadhaar card number"
                value={adharNumber}
                onChange={(e) => setAdharNumber(e.target.value)}
                disabled={disabledForm}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Mobile number</label>
              <PhoneNumberField
                variant="form"
                value={mobileNumber}
                onChange={(v) => {
                  setMobileNumber(v);
                  clearFieldError("mobile_number");
                }}
                disabled={disabledForm}
                hasError={!!errors.mobile_number}
              />
              {errors.mobile_number ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.mobile_number}</p>
              ) : null}
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Age</label>
              <input
                type="number"
                min={18}
                max={70}
                className={`form-control ${errors.age ? "border border-red-500" : ""}`}
                placeholder="Age"
                value={age}
                onChange={(e) => {
                  setAge(e.target.value);
                  clearFieldError("age");
                }}
                disabled={disabledForm}
              />
              {errors.age ? <p className="mt-1 mb-0 text-sm text-red-500">{errors.age}</p> : null}
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Accountant name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Accountant name"
                value={accountantName}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">MIPL location</label>
              <input
                type="text"
                className="form-control"
                placeholder="MIPL location"
                value={miplLocation}
                readOnly
                disabled
              />
            </div>
          </div>

          <h3 className="mb-2 md:text-xl font-UbuntuMedium">ID proof documents</h3>
          <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-3">
            <LicenseImageThumb
              previewUrl={licensePreviewSrc}
              imageError={licenseImageBroken}
              onImageError={() => setLicenseImageBroken(true)}
              label="Driver license (front)"
            />
            <LicenseImageThumb
              previewUrl={licenseBackPreviewSrc}
              imageError={licenseBackImageBroken}
              onImageError={() => setLicenseBackImageBroken(true)}
              label="Driver license (back)"
            />
            {errors.license_image ? (
              <p className="w-full mb-0 text-sm text-red-500">{errors.license_image}</p>
            ) : null}
            {otherDocuments.length > 0 ? (
              otherDocuments.map((doc, idx) => (
                <EditDocumentThumb key={`${doc?.document_type || "doc"}-${idx}`} doc={doc} />
              ))
            ) : null}
            {otherDocuments.length === 0 && !licensePreviewSrc && !licenseBackPreviewSrc ? (
              <p className="text-sm text-gray-500">No other document images on file.</p>
            ) : null}
            <div className="rounded-lg bg-[#00CE06] p-3 md:p-4 font-UbuntuMedium max-[767px]:mt-2">
              Fingerprint verified
            </div>
          </div>

          <div className="col-md-12 my-2 flex flex-wrap items-center gap-3 font-UbuntuRegular">
            <Link to={backToList} className="btn btn-primary" onClick={(e) => submitting && e.preventDefault()}>
              Cancel
            </Link>
            <button type="submit" className="btn btn-danger md:min-w-32" disabled={disabledForm}>
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditDriverDetails;
